import config from "../../../config/config";
import {
  authFetch,
  buildAuthHeaders,
  openScreenFetch,
} from "../../../public/api/httpClient";
import { normalizeMembershipOverview } from "../utils/myMembershipsOverviewHelpers";

const MEMBERSHIP_STUDENT_BASE = `${config.BASE_URL}/membership-student`;


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

/**
 * Get current membership details for logged-in student
 * @returns {Promise<Object>} - Membership details
 */
/**
 * Read-only overview from applications + renewals (optional auth).
 */
export const getMyMembershipsOverview = async () => {
  const res = await openScreenFetch(
    `${MEMBERSHIP_STUDENT_BASE}/my-memberships-overview`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    }
  );
  const data = await parseJson(res);
  if (res.status === 403 && data.code === "STUDENT_ROLE_REQUIRED") {
    const err = new Error(data.message || "Student login required");
    err.code = "STUDENT_ROLE_REQUIRED";
    throw err;
  }
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch membership overview");
  }

  let payments = [];
  if (res.ok && data?.data?.student?.id) {
    try {
      const payRes = await authFetch(`${MEMBERSHIP_STUDENT_BASE}/payments`, {
        credentials: "include",
        headers: buildAuthHeaders(),
      });
      const payData = await parseJson(payRes);
      if (payRes.ok && Array.isArray(payData?.data)) {
        payments = payData.data;
      }
    } catch {
      /* Overview still usable without payment history merge */
    }
  }

  const overview = normalizeMembershipOverview(data?.data, payments);
  return { ...data, data: overview };
};

export const getMyMembership = async () => {
  const res = await authFetch(`${MEMBERSHIP_STUDENT_BASE}/my-membership`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch membership details");
  }
  return data;
};

/**
 * Get payment history for logged-in student
 * @returns {Promise<Object>} - Payment history array
 */
export const getMyPaymentHistory = async () => {
  const res = await authFetch(`${MEMBERSHIP_STUDENT_BASE}/payments`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch payment history");
  }
  return data;
};

/**
 * Get payment receipt by ID
 * @param {number} paymentId - Payment ID
 * @returns {Promise<Object>} - Receipt details
 */
export const getReceipt = async (paymentId) => {
  const res = await authFetch(
    `${MEMBERSHIP_STUDENT_BASE}/receipt/${encodeURIComponent(String(paymentId))}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    }
  );
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch receipt");
  }
  return data;
};

/**
 * Get membership ID card
 * @returns {Promise<Object>} - ID card details
 */
export const getIdCard = async () => {
  const res = await authFetch(`${MEMBERSHIP_STUDENT_BASE}/id-card`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.message || "Failed to fetch ID card");
  }
  return data;
};
